import { useCallback, useRef, useState } from 'react';

// Utilità della trascrizione del verbale (piano 2): tempi leggibili, ricerca senza accenti né
// maiuscole, evidenziazione nel testo originale e segmento che contiene un tempo.

/** `h:mm:ss` dall'ora in su, sotto l'ora `m:ss`; valori non validi o negativi ⇒ `0:00`. */
export function formattaMs(ms: number): string {
  const totale = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const ore = Math.floor(totale / 3600);
  const minuti = Math.floor((totale % 3600) / 60);
  const secondi = totale % 60;
  const ss = String(secondi).padStart(2, '0');
  if (ore > 0) return `${ore}:${String(minuti).padStart(2, '0')}:${ss}`;
  return `${minuti}:${ss}`;
}

const SEGNI_DIACRITICI = /[̀-ͯ]/g;

/** Un carattere senza accenti e in minuscolo (può diventare vuoto o più lungo). */
const normalizzaCarattere = (c: string) => c.normalize('NFD').replace(SEGNI_DIACRITICI, '').toLowerCase();

/** Testo senza accenti e in minuscolo. */
export const normalizzaRicerca = (testo: string) =>
  (testo || '').normalize('NFD').replace(SEGNI_DIACRITICI, '').toLowerCase();

/** Ricerca normalizzata; `null` se è più corta di 2 caratteri (nessun filtro). */
function chiaveRicerca(q: string): string | null {
  const chiave = normalizzaRicerca((q || '').trim());
  return chiave.length < 2 ? null : chiave;
}

/** Indici dei segmenti il cui testo contiene `q` (senza accenti né maiuscole); `q` < 2 caratteri ⇒ tutti. */
export function cercaSegmenti(segmenti: { testo: string }[], q: string): number[] {
  const chiave = chiaveRicerca(q);
  const indici: number[] = [];
  segmenti.forEach((s, i) => {
    if (!chiave || normalizzaRicerca(s.testo).includes(chiave)) indici.push(i);
  });
  return indici;
}

export interface ParteTesto {
  testo: string;
  trovato: boolean;
}

/** Il testo diviso in parti, con `trovato` sulle occorrenze di `q` (cercate senza accenti né maiuscole). */
export function partiEvidenziate(testo: string, q: string): ParteTesto[] {
  const originale = testo || '';
  const chiave = chiaveRicerca(q);
  if (!chiave || !originale) return [{ testo: originale, trovato: false }];

  // Testo normalizzato con, per ogni suo carattere, l'inizio e la fine del carattere originale.
  let normalizzato = '';
  const inizi: number[] = [];
  const fini: number[] = [];
  let pos = 0;
  for (const c of originale) {
    const n = normalizzaCarattere(c);
    for (let k = 0; k < n.length; k++) {
      inizi.push(pos);
      fini.push(pos + c.length);
    }
    normalizzato += n;
    pos += c.length;
  }

  const parti: ParteTesto[] = [];
  let ultimo = 0;
  let da = normalizzato.indexOf(chiave);
  while (da >= 0) {
    const inizio = inizi[da];
    const fine = fini[da + chiave.length - 1];
    if (inizio >= ultimo) {
      if (inizio > ultimo) parti.push({ testo: originale.slice(ultimo, inizio), trovato: false });
      parti.push({ testo: originale.slice(inizio, fine), trovato: true });
      ultimo = fine;
    }
    da = normalizzato.indexOf(chiave, da + chiave.length);
  }
  if (ultimo < originale.length) parti.push({ testo: originale.slice(ultimo), trovato: false });
  return parti.length ? parti : [{ testo: originale, trovato: false }];
}

/** Indice del segmento che contiene `ms`; altrimenti l'ultimo iniziato prima; nessun segmento ⇒ -1. */
export function segmentoAlTempo(segmenti: { daMs: number; aMs: number }[], ms: number): number {
  if (!segmenti.length) return -1;
  const dentro = segmenti.findIndex((s) => ms >= s.daMs && ms < s.aMs);
  if (dentro >= 0) return dentro;
  let migliore = 0;
  segmenti.forEach((s, i) => {
    if (s.daMs <= ms) migliore = i;
  });
  return migliore;
}

/**
 * URL firmato «stabile» per un lettore audio: ogni lettura di `GET /audio` firma URL nuovi (validi
 * 6 h) e cambiare `src` a un `<audio>` lo riporta all'inizio. Si tiene il primo URL di ogni chiave
 * finché `rinnova()` (da chiamare su `onError`) non adotta l'ultimo ricevuto: true se è cambiato.
 */
export function useUrlStabile(chiave: string | null, url: string | null): [string | null, () => boolean] {
  const usatiRef = useRef<Map<string, string>>(new Map());
  const ultimoRef = useRef<string | null>(url);
  const [, aggiorna] = useState(0);
  ultimoRef.current = url;

  let usato: string | null = null;
  if (chiave && url) {
    const salvato = usatiRef.current.get(chiave);
    if (salvato) usato = salvato;
    else {
      usatiRef.current.set(chiave, url);
      usato = url;
    }
  }

  const rinnova = useCallback(() => {
    const ultimo = ultimoRef.current;
    if (!chiave || !ultimo || usatiRef.current.get(chiave) === ultimo) return false;
    usatiRef.current.set(chiave, ultimo);
    aggiorna((n) => n + 1);
    return true;
  }, [chiave]);

  return [usato, rinnova];
}
